import { create } from 'zustand'
import type { WorkspaceInvitation } from '@/api/types'
import type { AnnouncementContent, AnnouncementDelivery } from '@/api/types'
import { announcementApi } from '@/api/announcementApi'

export interface InvitationResponseNotif {
  id: string // `inv-${invitation_id}`
  invitation_id: number
  workspace_id: number
  workspace_name: string
  invitee_email: string
  invitee_name: string
  response: 'accepted' | 'declined'
  received_at: string
}

/** Notificacao de cobranca empurrada pelo webhook (ativacao ou falha de pagamento). */
export interface BillingNotif {
  id: string // `bill-${kind}-${workspace_id}-${received_at}`
  kind: 'activated' | 'payment_failed'
  workspace_id: number
  workspace_name: string
  workspace_slug: string
  plan_name: string
  reason: string
  received_at: string
}

interface NotificationsState {
  invitations: WorkspaceInvitation[]
  responses: InvitationResponseNotif[]
  billing: BillingNotif[]
  announcements: AnnouncementDelivery[]
  announcementTest: AnnouncementContent | null
  wsStatus: 'idle' | 'connecting' | 'connected' | 'disconnected'
  setInvitations: (invitations: WorkspaceInvitation[]) => void
  addInvitation: (invitation: WorkspaceInvitation) => void
  removeInvitation: (invitationId: number) => void
  addResponse: (notif: InvitationResponseNotif) => void
  dismissResponse: (id: string) => void
  clearResponses: () => void
  clearAll: () => Promise<void>
  addBilling: (notif: BillingNotif) => void
  dismissBilling: (id: string) => void
  loadAnnouncements: () => Promise<void>
  markAnnouncementSeen: (id: number) => Promise<void>
  dismissAnnouncement: (id: number) => Promise<void>
  setAnnouncementTest: (announcement: AnnouncementContent | null) => void
  setWsStatus: (status: NotificationsState['wsStatus']) => void
  reset: () => void
}

export const useNotificationsStore = create<NotificationsState>((set) => ({
  invitations: [],
  responses: [],
  billing: [],
  announcements: [],
  announcementTest: null,
  wsStatus: 'idle',

  setInvitations: (invitations) => set({ invitations }),

  addInvitation: (invitation) =>
    set((state) => {
      const exists = state.invitations.some((i) => i.id === invitation.id)
      if (exists) return state
      return { invitations: [invitation, ...state.invitations] }
    }),

  removeInvitation: (invitationId) =>
    set((state) => ({
      invitations: state.invitations.filter((i) => i.id !== invitationId),
    })),

  addResponse: (notif) =>
    set((state) => {
      const exists = state.responses.some((r) => r.id === notif.id)
      if (exists) return state
      return { responses: [notif, ...state.responses] }
    }),

  dismissResponse: (id) =>
    set((state) => ({
      responses: state.responses.filter((r) => r.id !== id),
    })),

  clearResponses: () => set({ responses: [] }),

  clearAll: async () => {
    await announcementApi.dismissAll()
    set({ responses: [], billing: [], announcements: [], announcementTest: null })
  },

  addBilling: (notif) =>
    set((state) => {
      const exists = state.billing.some((b) => b.id === notif.id)
      if (exists) return state
      return { billing: [notif, ...state.billing] }
    }),

  dismissBilling: (id) =>
    set((state) => ({
      billing: state.billing.filter((b) => b.id !== id),
    })),

  loadAnnouncements: async () => {
    const response = await announcementApi.inbox()
    set({ announcements: response.results })
  },

  markAnnouncementSeen: async (id) => {
    const delivery = await announcementApi.markSeen(id)
    set((state) => ({
      announcements: state.announcements.map((item) => (item.id === id ? delivery : item)),
    }))
  },

  dismissAnnouncement: async (id) => {
    await announcementApi.dismiss(id)
    set((state) => ({ announcements: state.announcements.filter((item) => item.id !== id) }))
  },

  setAnnouncementTest: (announcementTest) => set({ announcementTest }),

  setWsStatus: (wsStatus) => set({ wsStatus }),

  reset: () => set({
    invitations: [],
    responses: [],
    billing: [],
    announcements: [],
    announcementTest: null,
    wsStatus: 'idle',
  }),
}))
